import { type AccountNote, type AuditLog, isEmpty, isNotDefined, MINUTE } from '@terros/common'
import { type TeamChange, type UserChange } from './ownershipChanges'
import { isAreaAssignmentSave, isWrittenWith } from './historySource'
import { type AccountJournalEntry } from './accountJournal'
import { type TeamChangeActivity, type UserChangeActivity } from './accountActivity'

type ChangeActivity = UserChangeActivity | TeamChangeActivity

// Notes are stamped on the device before they are sent, so their save lands a little later
const NOTE_SAVE_DELAY = MINUTE

type WithAreaRunProps = {
  changes: ChangeActivity[]
  assignmentDate: number | undefined
}

/**
 * The account only remembers its latest area run (`assignmentDate`), so only the latest area-assignment
 * change can be traced back to who ran it. Owner and team changes from one run share a timestamp.
 */
export const withAreaRun = ({ changes, assignmentDate }: WithAreaRunProps): ChangeActivity[] => {
  if (isNotDefined(assignmentDate)) return changes
  // `changes` is grouped by owner, closer and team, so take the latest timestamp rather than the last entry
  const runChangeTimes = changes
    .filter((change) => isAreaAssignmentSave(change.actor) && change.timestamp >= assignmentDate)
    .map((change) => change.timestamp)
  if (isEmpty(runChangeTimes)) return changes
  const latestRunChangeAt = Math.max(...runChangeTimes)
  return changes.map((change) =>
    change.timestamp === latestRunChangeAt ? { ...change, areaRunAt: assignmentDate } : change
  )
}

type IsExplainedSaveProps = {
  audit: AuditLog
  journal: AccountJournalEntry[]
  notes: AccountNote[]
  changes: (UserChange | TeamChange)[]
}

export const isExplainedSave = ({ audit, journal, notes, changes }: IsExplainedSaveProps): boolean => {
  if (journal.some((entry) => isWrittenWith(audit, entry.timestamp))) return true
  if (changes.some((change) => change.timestamp === audit.timestamp)) return true
  return notes.some((note) => isNoteSave({ audit, note }))
}

const isNoteSave = ({ audit, note }: { audit: AuditLog; note: AccountNote }): boolean => {
  const delay = audit.timestamp - note.timestamp
  return audit.userId === note.userId && delay >= 0 && delay <= NOTE_SAVE_DELAY
}
